import type {
  NavigationGuardNext,
  NavigationGuardReturn,
  RouteLocationNormalized,
  Router
} from 'vue-router';

export type Middleware = (context: {
  to: RouteLocationNormalized;
  from: RouteLocationNormalized;
  next: NavigationGuardNext;
  router?: Router;
}) => NavigationGuardReturn | void | Promise<NavigationGuardReturn | void>;

/**
 * Middleware 管道
 * @param middleware - 單一或多個 Middleware
 * @param to - 目標路由
 * @param from - 來源路由
 * @param next - 導航守衛的 next 函式
 * @param router - Vue Router 實例
 */
export const middlewarePipeline = (
  middleware: Middleware | Middleware[],
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
  router?: Router
): void => {
  const middlewares: Middleware[] = Array.isArray(middleware) ? middleware : [middleware];

  /**
   * 中介函式調度器
   * @param i - 中介函式索引
   */
  const dispatch = (i = 0): void => {
    const mw = middlewares[i];
    if (!mw) return next();

    // 防止多次執行 next
    let settled = false;
    const nextMiddleware = ((...args: any[]) => {
      if (settled) return;
      settled = true;
      return args.length > 0 ? next(args[0]) : dispatch(i + 1);
    }) as unknown as NavigationGuardNext;

    /**
     * 異步執行中介函式
     */
    (async () => {
      try {
        const resolved = await mw({ to, from, next: nextMiddleware, router });
        if (settled) return;
        settled = true;
        return resolved !== undefined ? next(resolved as any) : dispatch(i + 1);
      } catch (error: unknown) {
        if (settled) return;
        settled = true;
        return next(error instanceof Error ? error : new Error(String(error)));
      }
    })();
  };

  dispatch();
};
